import { useState, useMemo } from 'react';
import { Calendar } from 'lucide-react';
import { ItemList } from './ItemList';
import { UndoButton } from './UndoButton';
import { List } from '@/types/lists';
import { useListsStore } from '@/store/listsStore';
import { cn } from '@/utils/cn';

interface ItemManagerProps {
  list: List;
  contextDate?: Date;
  className?: string;
}

export function ItemManager({ list, contextDate = new Date(), className }: ItemManagerProps) {

  // editMode controls both completed item visibility AND edit button visibility
  const [editMode, setEditMode] = useState(false);
  const { lists, getListDataForContext } = useListsStore();

  // Check if we're viewing a different date than today
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const contextDateNormalized = new Date(contextDate);
  contextDateNormalized.setHours(0, 0, 0, 0);
  const isViewingDifferentDate = contextDateNormalized.getTime() !== today.getTime();

  // Check if context has a meaningful time (not midnight - which is the default for date-only)
  const hasScheduledTime = contextDate.getHours() !== 0 || contextDate.getMinutes() !== 0;

  // Format the context date for display (include time if it's a scheduled event)
  const contextDateDisplay = hasScheduledTime
    ? contextDate.toLocaleString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      })
    : contextDate.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
  
  // Get contextual completion data - memoize to avoid unnecessary recalculations
  const listData = useMemo(() => {
    return getListDataForContext(list.id, contextDate);
  }, [list.id, contextDate, getListDataForContext, lists]); // Add lists dependency

  // Use contextual data if available, otherwise fall back to list items
  // Memoize expensive calculations
  const { totalItemsCount, completedItemsCount, visibleItems } = useMemo(() => {
    const items = listData ? listData.items : list.items;
    const total = listData ? listData.totalItems : list.items.length;
    const completed = listData ? listData.completedItems : 0;
    
    // Sort items by priority first, then filter completed items
    const sortedItems = [...items].sort((a, b) => a.priority - b.priority);
    
    // Simple filtering - just show/hide completed items
    const visible = editMode
      ? sortedItems
      : sortedItems.filter(item => !('completed' in item) || !item.completed);
    
    return {
      totalItemsCount: total,
      completedItemsCount: completed,
      visibleItems: visible
    };
  }, [listData, list.items, editMode]);

  // Debug logging to check if items are in correct order
  // useEffect(() => {
  //   console.log('🔍 ItemManager - Original items order:', 
  //     list.items.map(i => ({ id: i.id, content: i.content, priority: i.priority }))
  //   );
  //   console.log('✅ ItemManager - Sorted items order:', 
  //     visibleItems.map(i => ({ id: i.id, content: i.content, priority: i.priority }))
  //   );
  //   console.log('📊 ItemManager - Store lists updated, refreshing data');
  // }, [list.items, visibleItems, lists]); // Log when lists change

  return (
    <div className={cn('space-y-4', className)}>
      {/* Context Date Banner - show when viewing a different date/time than now */}
      {(isViewingDifferentDate || hasScheduledTime) && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-blue-600" />
            <span className="text-sm text-blue-800">
              Viewing items for: <strong>{contextDateDisplay}</strong>
            </span>
          </div>
        </div>
      )}

      {/* Items List */}
      <ItemList
        listId={list.id}
        items={visibleItems}
        contextDate={contextDate}
        showCompleted={editMode}
        onToggleShowCompleted={() => setEditMode(!editMode)}
        allowEditing={editMode}
        allowReordering={true}
        totalItemsCount={totalItemsCount}
        completedItemsCount={completedItemsCount}
      />

      {/* Undo Button */}
      <UndoButton />
    </div>
  );
}